import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import ChevronRight from '@mui/icons-material/ChevronRight';
import EventAvailableOutlined from '@mui/icons-material/EventAvailableOutlined';
import { AuthContext } from '../contexts/AuthContext';
import AppShell from '../components/layout/AppShell';
import { visibleNavItems } from '../components/layout/navConfig';
import { Button, Card, PageLoader, Alert } from '../components/ui';

const DASHBOARD_ROUTES = [
  '/qdf-main', '/add-shift-report', '/pas-interface',
  '/viewReports', '/add-monthly-report', '/system-report',
  '/maintenance-analysis', '/rca-main',
];

function HomePage() {
  const { role: contextRole, username: contextUsername } = useContext(AuthContext);
  const username = contextUsername || '';
  const role = contextRole || '';
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadingTooLong, setLoadingTooLong] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => setLoading(false), 250);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (!loading) return undefined;
    const timeoutId = setTimeout(() => setLoadingTooLong(true), 10000);
    return () => clearTimeout(timeoutId);
  }, [loading]);

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('userId');
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('userId');
    navigate('/login');
  };

  const now = new Date();
  const hour = now.getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const fullDate = new Intl.DateTimeFormat('en-GB', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  }).format(now);
  const visibleModules = new Map(visibleNavItems(role).map((item) => [item.to, item]));
  const modules = DASHBOARD_ROUTES.map((route) => visibleModules.get(route)).filter(Boolean);

  if (loading) {
    return <AppShell>
      <PageLoader label="Loading your dashboard…" />
      {loadingTooLong && <Alert variant="warning" title="Taking longer than expected" className="mx-auto mt-6 max-w-md"
        action={<Button variant="secondary" size="sm" onClick={handleLogout}>Log out and retry</Button>}>
        If this continues, signing out and back in usually clears it.
      </Alert>}
    </AppShell>;
  }

  return <AppShell>
    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0">
        <p className="text-sm text-muted-fg">{fullDate}</p>
        <h1 className="mt-1 truncate text-2xl font-semibold text-foreground sm:text-3xl">
          {greeting}{username ? `, ${username}` : ''}
        </h1>
      </div>
      <Button variant="secondary" size="md" icon={EventAvailableOutlined}
        onClick={() => navigate('/attendance')} className="self-start sm:self-auto">Attendance</Button>
    </div>
    <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {modules.map(({ to, label, description, icon: Icon }) => <Card key={to} interactive onClick={() => navigate(to)} className="group">
        <div className="flex items-start gap-4 p-5">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-accent-soft text-accent transition-colors duration-base group-hover:bg-accent group-hover:text-accent-fg">
            <Icon style={{ fontSize: 22 }} aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-semibold text-foreground">{label}</h2>
            <p className="mt-1 text-sm leading-relaxed text-muted-fg">{description}</p>
          </div>
          <ChevronRight style={{ fontSize: 20 }}
            className="mt-1 shrink-0 text-muted-fg transition-transform duration-base group-hover:translate-x-1 group-hover:text-accent"
            aria-hidden="true" />
        </div>
      </Card>)}
    </div>
  </AppShell>;
}

export default HomePage;
